import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { GoldCurrencyStatementService } from "services/gold-currency-statement";
import { formatVndIntegerInput, parseVndIntegerInput, formatVndIntegerValue } from "utils";
import { getPrintTemplate } from "./components/printTemplate";

const createRow = () => ({
  itemName: "",
  unit: "",
  quantity: "",
  unitPrice: "",
  amount: "",
  note: "",
});

const splitDateParts = (dateText = "") => {
  const [day = "", month = "", year = ""] = String(dateText).split("/");
  return {
    day: day.replace(/\D/g, "").slice(0, 2),
    month: month.replace(/\D/g, "").slice(0, 2),
    year: year.replace(/\D/g, "").slice(0, 4),
  };
};

const hasAnyDatePart = (parts) => Boolean(parts.day || parts.month || parts.year);
const hasAllDatePart = (parts) => Boolean(parts.day && parts.month && parts.year);

const composeDateOptional = (parts) => {
  if (!hasAnyDatePart(parts)) return "";
  if (!hasAllDatePart(parts)) throw new Error("Ngày đính kèm phiếu chưa nhập đủ ngày/tháng/năm");
  const day = Number(parts.day);
  const month = Number(parts.month);
  const year = Number(parts.year);
  const maxDay = new Date(year, month, 0).getDate();
  if (month < 1 || month > 12 || year < 1900 || year > 9999 || day < 1 || day > maxDay) {
    throw new Error("Ngày đính kèm phiếu không hợp lệ");
  }
  return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${String(year)}`;
};

const parseQty = (value) => {
  const normalized = String(value || "").replace(/,/g, ".").replace(/[^\d.]/g, "");
  if (!normalized) return 0;
  const numeric = Number(normalized);
  if (!Number.isFinite(numeric) || numeric < 0) return 0;
  return Number(numeric.toFixed(3));
};

const formatQtyInput = (value) => String(value || "").replace(/,/g, ".").replace(/[^\d.]/g, "");

const formatQtyOutput = (value) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return "";
  if (Math.abs(number - Math.trunc(number)) < 0.000001) return `${Math.trunc(number)}`;
  return number.toFixed(3).replace(/\.0+$/, "").replace(/(\.\d*?)0+$/, "$1");
};

const calcAmount = (quantity, unitPrice) => {
  const qty = parseQty(quantity);
  const price = parseVndIntegerInput(unitPrice);
  return Math.round(qty * price);
};

export default function GoldCurrencyStatementDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const today = new Date();
  const [attachedDateParts, setAttachedDateParts] = useState({
    day: String(today.getDate()).padStart(2, "0"),
    month: String(today.getMonth() + 1).padStart(2, "0"),
    year: String(today.getFullYear()),
  });

  const [header, setHeader] = useState({
    unit: "",
    department: "",
    attachedVoucher: "",
    bookNumber: "",
    documentNumber: "",
  });

  const [rows, setRows] = useState([createRow()]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await GoldCurrencyStatementService.getById(id);
        const data = response.data ? response.data : response;

        setHeader({
          unit: data.unit || "",
          department: data.department || "",
          attachedVoucher: data.attachedVoucher || "",
          bookNumber: data.bookNumber || "",
          documentNumber: data.documentNumber || "",
        });

        if (data.attachedVoucherDate) {
          setAttachedDateParts(splitDateParts(data.attachedVoucherDate || ""));
        }

        const mappedRows = (data.rows || []).map((row) => ({
          itemName: row.itemName || "",
          unit: row.unit || "",
          quantity: formatQtyOutput(row.quantity),
          unitPrice: formatVndIntegerInput(row.unitPrice || ""),
          amount: formatVndIntegerInput(row.amount || ""),
          note: row.note || "",
        }));

        setRows(mappedRows.length ? mappedRows : [createRow()]);
      } catch (error) {
        toast.error("Không thể tải dữ liệu bảng kê vàng tiền tệ");
      }
    })();
  }, [id]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => ({
        quantity: acc.quantity + parseQty(row.quantity),
        amount: acc.amount + parseVndIntegerInput(row.amount),
      }),
      { quantity: 0, amount: 0 }
    );
  }, [rows]);

  const setHeaderField = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const setAttachedDateField = (field, value) => {
    const max = field === "year" ? 4 : 2;
    const raw = value.replace(/\D/g, "");
    setAttachedDateParts((prev) => ({ ...prev, [field]: field === "year" ? raw.slice(0, max) : raw.slice(-max) }));
  };

  const padDateField = (field) => {
    if (field === "year") return;
    setAttachedDateParts((prev) => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: String(prev[field]).padStart(2, "0") };
    });
  };

  const updateRow = (index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      const current = { ...next[index] };
      if (field === "quantity") {
        current.quantity = formatQtyInput(value);
      } else if (field === "unitPrice") {
        current.unitPrice = formatVndIntegerInput(value);
      } else {
        current[field] = value;
      }

      if (field === "quantity" || field === "unitPrice") {
        const nextAmount = calcAmount(current.quantity, current.unitPrice);
        current.amount = nextAmount > 0 ? formatVndIntegerInput(nextAmount) : "";
      }

      next[index] = current;
      return next;
    });
  };

  const addRow = () => setRows((prev) => [...prev, createRow()]);

  const removeRow = (index) => {
    setRows((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const buildPayload = () => {
    const unit = String(header.unit || "").trim();
    const department = String(header.department || "").trim();
    const attachedVoucher = String(header.attachedVoucher || "").trim();
    const attachedVoucherDate = composeDateOptional(attachedDateParts);
    const bookNumber = String(header.bookNumber || "").trim();
    const documentNumber = String(header.documentNumber || "").trim();

    if (!unit) throw new Error("Đơn vị không được để trống");
    if (!department) throw new Error("Bộ phận không được để trống");
    if (!documentNumber) throw new Error("Số bảng kê không được để trống");

    const normalizedRows = rows
      .map((row, index) => ({
        serialNumber: String(index + 1),
        itemName: String(row.itemName || "").trim(),
        unit: String(row.unit || "").trim(),
        quantity: parseQty(row.quantity),
        unitPrice: parseVndIntegerInput(row.unitPrice),
        amount: parseVndIntegerInput(row.amount) || calcAmount(row.quantity, row.unitPrice),
        note: String(row.note || "").trim(),
      }))
      .filter((row) => {
        const hasQty = row.quantity > 0;
        const hasMoney = row.unitPrice > 0 || row.amount > 0;
        return row.itemName || row.unit || row.note || hasQty || hasMoney;
      });

    if (!normalizedRows.length) throw new Error("Bảng kê phải có ít nhất 1 dòng dữ liệu");

    normalizedRows.forEach((row, idx) => {
      if (!row.itemName) {
        throw new Error(`Dòng ${idx + 1}: Tên, loại, quy cách phẩm chất không được để trống`);
      }
    });

    return {
      unit,
      department,
      attachedVoucher,
      attachedVoucherDate,
      bookNumber,
      documentNumber,
      rows: normalizedRows,
    };
  };

  const submit = async () => {
    try {
      setLoadingSubmit(true);
      const payload = buildPayload();
      if (!id) {
        await GoldCurrencyStatementService.create(payload);
        toast.success("Tạo bảng kê vàng tiền tệ thành công");
      } else {
        await GoldCurrencyStatementService.update(id, payload);
        toast.success("Cập nhật bảng kê vàng tiền tệ thành công");
      }
      navigation("/templates/gold-currency-statement");
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Lưu thất bại");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handlePrint = () => {
    const htmlContent = getPrintTemplate({
      headerInfo: {
        ...header,
        attachedVoucherDate: composeDateOptional(attachedDateParts),
      },
      rows,
      totals,
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "16px",
    },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "16px",
      fontFamily: "inherit",
      background: "transparent",
      color: "#000",
      minWidth: 20,
    },
    inputDate: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "16px",
      fontFamily: "inherit",
      background: "transparent",
      color: "#000",
      width: 36,
      textAlign: "center",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      marginTop: 12,
    },
    th: {
      border: "1px solid #000",
      padding: "6px 4px",
      fontWeight: 700,
      textAlign: "center",
      fontSize: "15px",
    },
    td: {
      border: "1px solid #000",
      padding: "4px 6px",
      fontSize: "15px",
    },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox my={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card style={s.container}>
              <SoftBox>
                <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start">
                  <SoftBox>
                    <div>
                      <b>Đơn vị:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 220 }}
                        value={header.unit}
                        onChange={(event) => setHeaderField("unit", event.target.value)}
                      />
                    </div>
                    <div>
                      <b>Bộ phận:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 220 }}
                        value={header.department}
                        onChange={(event) => setHeaderField("department", event.target.value)}
                      />
                    </div>
                  </SoftBox>

                  <SoftBox textAlign="center">
                    <div>
                      <b>Mẫu số: 06 - TT</b>
                    </div>
                    <div style={{ fontStyle: "italic", fontSize: 13 }}>(Kèm theo Thông tư số 99/2025/TT-BTC</div>
                    <div style={{ fontStyle: "italic", fontSize: 13 }}>ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</div>
                  </SoftBox>
                </SoftBox>

                <SoftBox textAlign="center" mt={3} mb={1}>
                  <div style={{ fontWeight: 700, fontSize: 20 }}>BẢNG KÊ VÀNG TIỀN TỆ</div>
                </SoftBox>

                <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" mt={1}>
                  <div style={{ fontStyle: "italic" }}>
                    (Đính kèm phiếu{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 180 }}
                      value={header.attachedVoucher}
                      onChange={(event) => setHeaderField("attachedVoucher", event.target.value)}
                    />
                    <div style={{ marginTop: 4 }}>
                      Ngày{" "}
                      <input
                        disabled={containView}
                        style={s.inputDate}
                        value={attachedDateParts.day}
                        onChange={(event) => setAttachedDateField("day", event.target.value)}
                        onBlur={() => padDateField("day")}
                      />{" "}
                      tháng{" "}
                      <input
                        disabled={containView}
                        style={s.inputDate}
                        value={attachedDateParts.month}
                        onChange={(event) => setAttachedDateField("month", event.target.value)}
                        onBlur={() => padDateField("month")}
                      />{" "}
                      năm{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDate, width: 60 }}
                        value={attachedDateParts.year}
                        onChange={(event) => setAttachedDateField("year", event.target.value)}
                      />)
                    </div>
                  </div>

                  <div style={{ lineHeight: 1.8, textAlign: "right" }}>
                    <div>
                      Quyển số:{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 160 }}
                        value={header.bookNumber}
                        onChange={(event) => setHeaderField("bookNumber", event.target.value)}
                      />
                    </div>
                    <div>
                      Số:{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 160 }}
                        value={header.documentNumber}
                        onChange={(event) => setHeaderField("documentNumber", event.target.value)}
                      />
                    </div>
                  </div>
                </SoftBox>

                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={{ ...s.th, width: 50 }}>STT</th>
                      <th style={s.th}>Tên, loại, quy cách phẩm chất</th>
                      <th style={{ ...s.th, width: 90 }}>Đơn vị tính</th>
                      <th style={{ ...s.th, width: 90 }}>Số lượng</th>
                      <th style={{ ...s.th, width: 120 }}>Đơn giá</th>
                      <th style={{ ...s.th, width: 120 }}>Thành tiền</th>
                      <th style={{ ...s.th, width: 140 }}>Ghi chú</th>
                      {!containView && <th style={{ ...s.th, width: 36 }}>#</th>}
                    </tr>
                    <tr>
                      <th style={s.th}>A</th>
                      <th style={s.th}>B</th>
                      <th style={s.th}>C</th>
                      <th style={s.th}>1</th>
                      <th style={s.th}>2</th>
                      <th style={s.th}>3</th>
                      <th style={s.th}>D</th>
                      {!containView && <th style={s.th}>#</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr key={`row-${index}`}>
                        <td style={{ ...s.td, textAlign: "center" }}>{index + 1}</td>
                        <td style={s.td}>
                          <input
                            disabled={containView}
                            style={{ ...s.inputDotted, width: "100%" }}
                            value={row.itemName}
                            onChange={(event) => updateRow(index, "itemName", event.target.value)}
                          />
                        </td>
                        <td style={{ ...s.td, textAlign: "center" }}>
                          <input
                            disabled={containView}
                            style={{ ...s.inputDotted, width: "100%", textAlign: "center" }}
                            value={row.unit}
                            onChange={(event) => updateRow(index, "unit", event.target.value)}
                          />
                        </td>
                        <td style={{ ...s.td, textAlign: "right" }}>
                          <input
                            disabled={containView}
                            style={{ ...s.inputDotted, width: "100%", textAlign: "right" }}
                            value={row.quantity}
                            onChange={(event) => updateRow(index, "quantity", event.target.value)}
                          />
                        </td>
                        <td style={{ ...s.td, textAlign: "right" }}>
                          <input
                            disabled={containView}
                            inputMode="numeric"
                            style={{ ...s.inputDotted, width: "100%", textAlign: "right" }}
                            value={row.unitPrice}
                            onChange={(event) => updateRow(index, "unitPrice", event.target.value)}
                          />
                        </td>
                        <td style={{ ...s.td, textAlign: "right" }}>
                          <input
                            disabled
                            style={{ ...s.inputDotted, width: "100%", textAlign: "right" }}
                            value={row.amount}
                            readOnly
                          />
                        </td>
                        <td style={s.td}>
                          <input
                            disabled={containView}
                            style={{ ...s.inputDotted, width: "100%" }}
                            value={row.note}
                            onChange={(event) => updateRow(index, "note", event.target.value)}
                          />
                        </td>
                        {!containView && (
                          <td style={{ ...s.td, textAlign: "center" }}>
                            <button type="button" onClick={() => removeRow(index)} style={{ border: "none", background: "none", color: "red", cursor: "pointer" }}>
                              x
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                    <tr>
                      <td style={{ ...s.td, textAlign: "center" }} />
                      <td style={{ ...s.td, textAlign: "center", fontWeight: 700 }}>Cộng</td>
                      <td style={s.td} />
                      <td style={{ ...s.td, textAlign: "right", fontWeight: 700 }}>{formatQtyOutput(totals.quantity)}</td>
                      <td style={s.td} />
                      <td style={{ ...s.td, textAlign: "right", fontWeight: 700 }}>{formatVndIntegerValue(totals.amount)}</td>
                      <td style={s.td} />
                      {!containView && <td style={s.td} />}
                    </tr>
                  </tbody>
                </table>

                {!containView && (
                  <SoftBox mt={2} display="flex" gap={2} justifyContent="flex-end">
                    <SoftButton variant="outlined" color="dark" onClick={addRow}>
                      THÊM DÒNG
                    </SoftButton>
                    <SoftButton variant="outlined" color="error" onClick={() => removeRow(rows.length - 1)}>
                      XÓA DÒNG
                    </SoftButton>
                  </SoftBox>
                )}

                <SoftBox mt={3} display="flex" justifyContent="space-between">
                  <SoftButton variant="outlined" color="secondary" onClick={() => navigation("/templates/gold-currency-statement")}>TRỞ VỀ</SoftButton>
                  <SoftBox display="flex" gap={1}>
                    {containView && (
                      <SoftButton variant="gradient" color="success" onClick={handlePrint}>
                        IN BẢNG
                      </SoftButton>
                    )}
                    {!containView && (
                      <SoftButton variant="gradient" color="dark" onClick={submit} disabled={loadingSubmit}>
                        {loadingSubmit ? "ĐANG LƯU..." : id ? "LƯU" : "TẠO"}
                      </SoftButton>
                    )}
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
